import { Button, FormMessage, IconButton, Input } from "@/atoms";
import ListSelector from "@/forms/selectors/listselector";
import { Description } from "@/globals";
import H2 from "@/headings/h2";
import z from "zod";

import { TargetKind } from "@oneleet/api_client";

import { PlusIcon, XIcon } from "lucide-react";

interface Props {
  value: Array<Target>;
  className?: string;
  error?: string;
  onChange: (targets: Array<Target>) => void;
}

const targetKindSchema = z.nativeEnum(TargetKind);

// eslint-disable-next-line react-refresh/only-export-components
export const targetSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal(TargetKind.HOSTPORT),
    hostPort: z.string(),
  }),
  z.object({
    kind: z.literal(TargetKind.FILEPATH),
    filepath: z.string(),
  }),
]);

export type Target = z.infer<typeof targetSchema>;

export default function TargetsList({
  value,
  onChange,
  error,
  className,
}: Props) {
  const addNewTarget = () => {
    const newTargets = value.map((target) => target);
    newTargets.push({
      kind: TargetKind.HOSTPORT,
      hostPort: "",
    });

    onChange(newTargets);
  };

  const updateTargetKind = (index: number, kind: TargetKind) => {
    const newTargets = value.map((target) => target);

    let target = newTargets[index];

    if (target.kind === kind) {
      return;
    }

    if (kind === TargetKind.HOSTPORT && target.kind === TargetKind.FILEPATH) {
      target = {
        kind: TargetKind.HOSTPORT,
        hostPort: target.filepath,
      };
    }

    if (kind === TargetKind.FILEPATH && target.kind === TargetKind.HOSTPORT) {
      target = {
        kind: TargetKind.FILEPATH,
        filepath: target.hostPort,
      };
    }

    // replace the target
    newTargets[index] = target;

    onChange(newTargets);
  };

  const updateTargetText = (index: number, text: string) => {
    const newTargets = value.map((target) => target);

    const target = newTargets[index];

    if (target.kind === TargetKind.HOSTPORT) {
      target.hostPort = text;
    } else {
      target.filepath = text;
    }

    // replace the target
    newTargets[index] = target;

    onChange(newTargets);
  };

  const removeTarget = (index: number) => {
    const newTargets = value.map((target) => target);

    newTargets.splice(index, 1);

    onChange(newTargets);
  };

  return (
    <div className={className}>
      <H2 title="Targets" />
      <Description className="mb-2">
        A list of targets (endpoints or files) that are impacted by this
        finding.
      </Description>
      <div className="mt-2">
        <div className="flex flex-col gap-2">
          {value.map((target, index) => {
            return (
              <div className="flex flex-row gap-2" key={index}>
                <ListSelector
                  options={[
                    { label: "Host", value: TargetKind.HOSTPORT },
                    { label: "File", value: TargetKind.FILEPATH },
                  ]}
                  placeholder="Target kind"
                  value={target.kind}
                  onChange={(kind) => {
                    const newKind = targetKindSchema.parse(kind);
                    updateTargetKind(index, newKind);
                  }}
                />
                <div className="w-full">
                  <Input
                    onChange={(e) => updateTargetText(index, e.target.value)}
                    value={
                      target.kind === TargetKind.FILEPATH
                        ? target.filepath
                        : target.hostPort
                    }
                  />
                </div>
                <IconButton
                  icon={<XIcon />}
                  onClick={() => removeTarget(index)}
                />
              </div>
            );
          })}
        </div>
      </div>

      <Button
        className={value.length > 0 ? "mt-4" : ""}
        label="Add target"
        variant="soft"
        color="brand"
        startIcon={<PlusIcon />}
        onClick={() => {
          addNewTarget();
        }}
      />

      {error && (
        <div className="mt-2">
          <FormMessage text={error} color="red" />
        </div>
      )}
    </div>
  );
}
